import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import CRAPage from "@/pages/CRAPage";
import MissionsPage from "@/pages/MissionsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import ConsultantsPage from "@/pages/ConsultantsPage";
import ClientsPage from "@/pages/ClientsPage";
import CRMPage from "@/pages/CRMPage";
import SettingsPage from "@/pages/SettingsPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (must be synchronous, before render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/cra" element={
        <ProtectedRoute>
          <CRAPage />
        </ProtectedRoute>
      } />
      
      <Route path="/missions" element={
        <ProtectedRoute>
          <MissionsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/invoices" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/consultants" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <ConsultantsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <ClientsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/crm" element={
        <ProtectedRoute allowedRoles={['admin', 'manager']}>
          <CRMPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
